import { useEffect, useRef, useState } from 'react';

import {
  clientErrorTracker,
  useSoyuzClientConfig
} from '@spotahome/soyuz/client';

import debounce from '../../utils/debounce';

import { normalizeLocalityToCityId } from './utils';

const AUTOCOMPLETE_URL = 'https://api.woosmap.com/localities/autocomplete/';
const DETAILS_URL = 'https://api.woosmap.com/localities/details';

const extractCityId = addressComponents => {
  const locality = addressComponents?.find(c => c.types?.includes('locality'));
  return normalizeLocalityToCityId(locality?.long_name || '');
};

const useAutocompleteService = ({
  countryCode,
  boundCoords,
  pointOfInterest,
  onPlaceSelected
}) => {
  const { woosmapApiKey } = useSoyuzClientConfig();
  const placesRef = useRef(null);
  const [inputValue, setInputValue] = useState(pointOfInterest?.name);
  const [suggestionList, setSuggestionList] = useState([]);
  const [suggestionDescriptions, setSuggestionDescriptions] = useState([]);
  const placesMapRef = useRef({});

  const closeSuggestions = e => {
    if (placesRef.current && !placesRef.current.contains(e.target)) {
      setSuggestionList([]);
      setSuggestionDescriptions([]);
    }
  };

  useEffect(() => {
    document.addEventListener('click', closeSuggestions);
    return () => document.removeEventListener('click', closeSuggestions);
  }, []);

  useEffect(() => {
    setInputValue(pointOfInterest ? pointOfInterest.name : '');
  }, [pointOfInterest]);

  const requestSuggestions = useRef(
    debounce(async value => {
      if (value.length <= 2 || !woosmapApiKey) {
        placesMapRef.current = {};
        setSuggestionList([]);
        setSuggestionDescriptions([]);
        return;
      }

      try {
        const params = new URLSearchParams({
          input: value,
          key: woosmapApiKey,
          language: document.documentElement.lang || 'en',
          types: 'locality|postal_code|admin_level|address'
        });

        if (boundCoords) {
          params.set(
            'location',
            `${parseFloat(boundCoords.lat)},${parseFloat(boundCoords.lng)}`
          );
        }

        if (countryCode) {
          params.set('components', `country:${countryCode}`);
        }

        const response = await fetch(`${AUTOCOMPLETE_URL}?${params}`);
        const data = await response.json();

        const localities = data?.localities || [];
        if (localities.length === 0) {
          placesMapRef.current = {};
          setSuggestionList([]);
          setSuggestionDescriptions([]);
          return;
        }

        const newPlacesMap = {};
        const formatted = localities.map(loc => {
          const matched = loc.matched_substrings?.description?.[0] || {
            offset: 0,
            length: value.length
          };
          newPlacesMap[loc.description] = loc;
          return {
            public_id: loc.public_id,
            description: loc.description,
            place_id: loc.public_id,
            structured_formatting: {
              main_text: loc.description,
              secondary_text: '',
              main_text_matched_substrings: [
                { offset: matched.offset, length: matched.length }
              ]
            }
          };
        });

        placesMapRef.current = newPlacesMap;
        setSuggestionList(formatted);
        setSuggestionDescriptions(formatted.map(r => r.description));
      } catch {
        placesMapRef.current = {};
        setSuggestionList([]);
        setSuggestionDescriptions([]);
      }
    }, 300)
  );

  const handleInput = e => {
    const value = e?.target?.value ?? e;
    setInputValue(value);
    requestSuggestions.current(value);
  };

  const setSelectedValue = placeSelected => {
    setInputValue(placeSelected.description || placeSelected.name);
    setSuggestionList([]);
    setSuggestionDescriptions([]);
    placesMapRef.current = {};
    onPlaceSelected(placeSelected);
  };

  const retrievePlace = async publicId => {
    const params = new URLSearchParams({
      public_id: publicId,
      key: woosmapApiKey
    });
    const response = await fetch(`${DETAILS_URL}?${params}`);
    return response.json();
  };

  const buildPlace = (publicId, description, result) => {
    const location = result?.geometry?.location;
    if (!location) return null;

    return {
      placeId: publicId,
      cityId: extractCityId(result.address_components),
      lat: location.lat,
      lng: location.lng,
      name: description
    };
  };

  const resetSuggestions = () => {
    setSuggestionList([]);
    setSuggestionDescriptions([]);
    placesMapRef.current = {};
  };

  const trackRetrieveFailure = error => {
    clientErrorTracker.error({
      error,
      source: 'MapProvider-Autocomplete-Woosmap'
    });
  };

  const handleSelect = (placeId, description) => async () => {
    if (!placeId) return;
    try {
      const data = await retrievePlace(placeId);
      const place = buildPlace(placeId, description, data?.result);
      if (place) setSelectedValue(place);
    } catch (error) {
      trackRetrieveFailure(error);
      resetSuggestions();
    }
  };

  const handlePlaceSelected = async selectedDescription => {
    const suggestion = placesMapRef.current[selectedDescription];
    if (!suggestion?.public_id) return;

    try {
      const data = await retrievePlace(suggestion.public_id);
      const place = buildPlace(
        suggestion.public_id,
        selectedDescription,
        data?.result
      );
      if (!place) return;

      setInputValue(selectedDescription);
      resetSuggestions();
      onPlaceSelected(place);
    } catch (error) {
      trackRetrieveFailure(error);
      resetSuggestions();
    }
  };

  const handleInputChange = handleInput;

  return {
    handleInput,
    handleInputChange,
    suggestionList,
    suggestionDescriptions,
    inputValue,
    placesRef,
    handleSelect,
    handlePlaceSelected,
    placesMapRef
  };
};

export default useAutocompleteService;
